import React, { useEffect, useRef, useState } from 'react';
import { authAPI } from '../services/api';
import BrandLogo from './BrandLogo';
import { useCoachExperience, isCoachRequestCancelled } from '../context/CoachExperienceContext';

// Self-serve onboarding: a signed-in coach with no club creates their own club
// (and optionally a first team) here, becomes club_admin, and re-enters the app.
// Auth is cookie-based (HttpOnly), so we persist only user profile locally. Dormant behind REACT_APP_SELF_SERVE_ONBOARDING; until then
// club-less coaches keep seeing the invite-only NoClubGate.
export default function CreateClubWizard({ user, onComplete, onLogout, onBack, initialContext }) {
  const coaching = useCoachExperience();
  const starterIntake = initialContext || (coaching?.data?.flags?.starter ? coaching.data.draft?.intake : null);
  const [clubName, setClubName] = useState('');
  const [teamName, setTeamName] = useState('');
  const [ageGroup, setAgeGroup] = useState(starterIntake?.ageGroup || '');
  const [sport, setSport] = useState(starterIntake?.sport || 'football');
  const [location, setLocation] = useState('');
  const [authorityAcknowledged, setAuthorityAcknowledged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uncertain, setUncertain] = useState(false);
  const inFlight = useRef(false);
  const statusMessage = useRef(null);
  const errorMessage = useRef(null);
  const checkButton = useRef(null);
  const starterPrefilled = useRef(Boolean(starterIntake));
  const editedStarterFields = useRef(new Set());
  const ownerId = String(user?.id || user?._id || '');
  const ownRequest = work => coaching?.run
    ? coaching.run(work) : work({ headers: { 'X-Coach-Owner': ownerId } });

  useEffect(() => {
    if (loading) statusMessage.current?.focus();
    else if (uncertain) checkButton.current?.focus();
    else if (error) errorMessage.current?.focus();
  }, [loading, uncertain, error]);
  useEffect(() => {
    if (!starterIntake || starterPrefilled.current) return;
    starterPrefilled.current = true;
    if (!editedStarterFields.current.has('sport')) setSport(starterIntake.sport);
    if (!editedStarterFields.current.has('ageGroup')) setAgeGroup(starterIntake.ageGroup);
  }, [starterIntake]);

  const inputStyle = {
    width: '100%', padding: '12px', marginBottom: '12px', borderRadius: '8px',
    border: '1px solid var(--border-input)', background: 'var(--bg-input)',
    color: 'var(--text-primary)', fontSize: '16px', boxSizing: 'border-box',
  };

  const checkSetupStatus = async () => {
    setLoading(true);
    try {
      const response = await ownRequest(options => authAPI.getProfile(options));
      const fresh = response.data?.user || response.data?.data || response.data;
      if (String(fresh?.id || fresh?._id || '') !== ownerId) {
        setError('Your sign-in changed. Reload before continuing.');
        return;
      }
      if (typeof fresh.hasClub !== 'boolean') {
        setUncertain(true);
        setError('The setup result could not be verified. Check its status before submitting again.');
        return;
      }
      if (fresh?.hasClub) {
        localStorage.setItem('user', JSON.stringify(fresh));
        onComplete(fresh);
      } else {
        setUncertain(false);
        setError('Your club setup is not complete. Your entries are still here; review them before trying again.');
      }
    } catch (err) {
      if (isCoachRequestCancelled(err)) return;
      setError('We could not check whether setup finished. Check its status before submitting again.');
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = clubName.trim();
    if (!name) { setError('Club name is required.'); return; }
    if (!location.trim()) { setError('Club location is required.'); return; }
    if (!authorityAcknowledged) {
      setError('Please confirm that your club is authorised to manage its player data.');
      return;
    }
    if (inFlight.current || uncertain) return;
    inFlight.current = true;
    setLoading(true);
    setError('');
    try {
      const res = await ownRequest(options => authAPI.createClub({
        clubName: name,
        teamName: teamName.trim(),
        ageGroup: ageGroup.trim(),
        ...(starterIntake ? { sport: sport.trim() } : {}),
        location: location.trim(),
        authorityAcknowledged,
        authorityVersion: '2026-07-10',
      }, options));
      const { user: freshUser } = res.data;
      if (!freshUser || String(freshUser.id || freshUser._id || '') !== ownerId) {
        setUncertain(true);
        await checkSetupStatus();
        return;
      }
      localStorage.setItem('user', JSON.stringify(freshUser));
      onComplete(freshUser);
    } catch (err) {
      if (isCoachRequestCancelled(err)) return;
      if (!err.response || err.response.status >= 500 || err.response.data?.code === 'ALREADY_HAS_CLUB') {
        setUncertain(true);
        setError('The setup response was lost. Checking whether your club was created...');
        await checkSetupStatus();
        return;
      }
      const duplicate = err.response?.data?.code === 'DUPLICATE_CLUB';
      setError(duplicate
        ? 'That club already exists. Go back and request to join it instead.'
        : (err.response?.data?.error || 'Could not create your club. Please try again.'));
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--bg-primary)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, sans-serif',
      padding: '16px', boxSizing: 'border-box',
    }}>
      <div style={{
        background: 'var(--bg-card)', padding: window.innerWidth < 480 ? '24px' : '40px',
        borderRadius: '16px', width: '90%', maxWidth: '480px',
        border: '1px solid var(--border-color)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <BrandLogo height={42} showBeta />
        </div>
        <div style={{ fontSize: '48px', marginBottom: '12px', textAlign: 'center' }} aria-hidden="true">🏟️</div>
        <h1 style={{ color: 'var(--accent)', fontSize: '22px', marginBottom: '8px', textAlign: 'center' }}>
          Create your club
        </h1>
        <p style={{ color: 'var(--text-dim)', fontSize: '13px', marginBottom: '24px', textAlign: 'center', lineHeight: 1.5 }}>
          Welcome, {user?.name || 'Coach'}! Set up your club to start adding teams,
          players, and sessions. You can change these details later.
        </p>

        <p role="status" aria-live="polite" aria-atomic="true" tabIndex={-1} ref={statusMessage}>
          {loading ? uncertain ? 'Checking whether your club setup finished...' : 'Creating your club...' : ''}
        </p>
        <div aria-live="assertive" aria-atomic="true" tabIndex={-1} ref={errorMessage} style={{ minHeight: error ? 'auto' : 0 }}>
          {error && (
            <div style={{
              background: '#450a0a', border: '1px solid #dc2626', color: '#fca5a5',
              padding: '10px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px',
            }}>{error}</div>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          {onBack && (
            <button type="button" onClick={onBack} style={{
              padding: '0 0 14px', background: 'transparent', color: 'var(--accent)',
              border: 'none', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
            }}>
              ← Back to club choices
            </button>
          )}
          <label htmlFor="cw-club" style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '6px' }}>
            Club name <span style={{ color: 'var(--accent)' }}>*</span>
          </label>
          <input id="cw-club" type="text" value={clubName} maxLength={200} autoFocus
            onChange={e => setClubName(e.target.value)}
            placeholder="e.g. Riverside Soccer Club" style={inputStyle} />

          <label htmlFor="cw-team" style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '6px' }}>
            First team name <span style={{ color: 'var(--text-dim)' }}>(optional)</span>
          </label>
          <input id="cw-team" type="text" value={teamName} maxLength={100}
            onChange={e => setTeamName(e.target.value)}
            placeholder="e.g. U12 Eagles" style={inputStyle} />

          <label htmlFor="cw-age" style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '6px' }}>
            Age group <span style={{ color: 'var(--text-dim)' }}>(optional)</span>
          </label>
          <input id="cw-age" type="text" value={ageGroup} maxLength={50}
            onChange={e => { editedStarterFields.current.add('ageGroup'); setAgeGroup(e.target.value); }}
            placeholder="e.g. U12" style={inputStyle} />

          {starterIntake && <>
            <label htmlFor="cw-sport" style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '6px' }}>Sport (from your coaching notes)</label>
            <input id="cw-sport" value={sport} onChange={event => { editedStarterFields.current.add('sport'); setSport(event.target.value); }} required maxLength={40} style={inputStyle} />
          </>}

          <label htmlFor="cw-loc" style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '6px' }}>
            Location <span style={{ color: 'var(--accent)' }}>*</span>
          </label>
          <input id="cw-loc" type="text" value={location} maxLength={120}
            onChange={e => setLocation(e.target.value)}
            placeholder="City or region" required style={{ ...inputStyle, marginBottom: '20px' }} />

          <label htmlFor="cw-authority" style={{
            display: 'flex', gap: '8px', alignItems: 'flex-start', color: 'var(--text-secondary)',
            fontSize: '12px', lineHeight: 1.45, marginBottom: '20px'
          }}>
            <input id="cw-authority" type="checkbox" checked={authorityAcknowledged}
              onChange={e => setAuthorityAcknowledged(e.target.checked)} required />
            <span>I confirm that my club is authorised to manage its player data and will provide any notices or permissions required by applicable law.</span>
          </label>

          {uncertain && <button type="button" ref={checkButton} onClick={checkSetupStatus} disabled={loading} style={{ minHeight: 44, marginBottom: 12 }}>Check setup status</button>}
          <button type="submit" disabled={loading || uncertain} style={{
            width: '100%', padding: '12px',
            background: loading ? 'var(--border-input)' : 'var(--accent)',
            color: 'var(--text-on-accent)', border: 'none', borderRadius: '8px',
            fontSize: '15px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer',
          }}>
            {loading ? 'Creating…' : 'Create club'}
          </button>
        </form>

        <div style={{
          background: 'var(--bg-input)', border: '1px solid var(--border-input)',
          borderRadius: '12px', padding: '12px', margin: '20px 0 16px',
        }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px', margin: 0 }}>
            📧 Signed in as <strong style={{ color: 'var(--text-primary)' }}>{user?.email}</strong>
          </p>
        </div>

        <div style={{ textAlign: 'center' }}>
          <button onClick={onLogout} style={{
            padding: '8px 20px', background: 'transparent', color: 'var(--text-secondary)',
            border: '1px solid var(--border-input)', borderRadius: '8px',
            fontSize: '13px', fontWeight: '600', cursor: 'pointer',
          }}>
            Logout
          </button>
        </div>

        <p style={{ color: 'var(--text-dim)', fontSize: '11px', marginTop: '20px', textAlign: 'center', lineHeight: 1.5 }}>
          Waiting on an invite instead? Open your invite link in this browser while signed in.
        </p>
      </div>
    </div>
  );
}
